import React, { useState, useEffect } from 'react';
import SearchBar from '../components/SearchBar/SearchBar';
import MonthPicker from '../components/DatePicker/MonthPicker';
import FilterButton from '../components/FilterButton/FilterButton';
import './ReviewSuppliers.scss';  // Import the stylesheet
import './FollowUpPage.scss'
import { API } from '../data/API/API'; // Import the API call logic
import { format } from 'date-fns'; // Import date formatting
import SupplierTable from '../components/SupplierTable/SupplierTable';
import { Button, ButtonGroup, Modal, Spinner, ToggleButton } from 'react-bootstrap'; // Import Spinner from react-bootstrap
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle,faSyncAlt, faCheckCircle, faExclamationTriangle, faClock } from '@fortawesome/free-solid-svg-icons';
import AValueInfoModal from '../components/AValueInfoModal/AValueInfoModal';
import { NullReportingRun, NullReportingSnapshot } from '../data/API/Nullreporting';
import NullReportingTable from '../components/NullReporting/NullReportingTable';
import { NullReportingReviewsAPI } from '../data/API/NullReportingReview';
type ActiveView = 'avvikelse' | 'noll';

const formatLastRunTime = (endedAt?: string | null, startedAt?: string | null) => {
  const raw = endedAt || startedAt;
  if (!raw) return "—";
  const d = new Date(raw);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const getRunStatusDetails = (code: number | string | null | undefined) => {
  const c = String(code ?? "");
  switch (c) {
    case "1":
      return {
        text: "Körs...",
        colorClass: "nr-runstatus--running",
        icon: <FontAwesomeIcon icon={faClock} />,
      };
    case "2":
      return {
        text: "OK",
        colorClass: "nr-runstatus--ok",
        icon: <FontAwesomeIcon icon={faCheckCircle} />,
      };
    case "3":
      return {
        text: "Misslyckades",
        colorClass: "nr-runstatus--failed",
        icon: <FontAwesomeIcon icon={faExclamationTriangle} />,
      };
    default:
      return {
        text: "Okänd",
        colorClass: "nr-runstatus--unknown",
        icon: null,
      };
  }
};
const ReviewSuppliers = () => {
    interface Supplier {
        supplierName: string;
        orgNumber: string;
        aValue: string;
        staticAValue: string;
        period: string; // Ensure 'Period' is fetched
        thresholdYellow?: number; // Add optional thresholdYellow
        thresholdRed?: number; // Add optional thresholdRed
    }
    const [activeView, setActiveView] = useState<ActiveView>('avvikelse');
    const [nullSnapshots, setNullSnapshots] = useState<NullReportingSnapshot[]>([]);
    const [nullLastRun, setNullLastRun] = useState<NullReportingRun | null>(null);
    const [nullLoading, setNullLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [supplierAPIData, setSupplierAPIData] = useState<Supplier[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
        const currentDate = new Date();
        currentDate.setMonth(currentDate.getMonth() - 2); // Set to two months earlier
        return currentDate;
    });

    const [filter, setFilter] = useState<string>('');
    const [showAValueInfoModal, setAValueInfoModal] = useState(false);
    const fetchNullReporting = async (formattedDate: string) => {
  setNullLoading(true);
  try {
    // const res = await API.NullReporting.GetSnapshots(formattedDate);
    // setNullSnapshots(res.snapshots || []);
    const res = await API.NullReporting.GetSnapshots(formattedDate);
const summary = await NullReportingReviewsAPI.summaryForPeriod(formattedDate);

const map = new Map(summary.map(s => [s.supplierOrgNr, s]));
const merged = (res.snapshots || []).map((row: any) => {
  const s = map.get(row.supplierOrgId);
  return {
    ...row,
    reviewTotalCount: s?.totalCount ?? 0,
    reviewOpenCount: s?.openCount ?? 0,
    reviewLatestId: s?.latestId ?? null,
    reviewLatestStatus: s?.latestStatus ?? null,
    reviewLatestType: s?.latestType ?? 0, // 0=email, 1=phone
  };
});
    setNullSnapshots(merged);
    setNullLastRun(res.lastRun || null);
  } catch (err: any) {
    setError(err?.message ?? 'Failed to fetch null reporting');
  } finally {
    setNullLoading(false);
  }
};

    // Function to fetch supplier data based on selected date (month)
    useEffect(() => {
        const fetchSupplierData = async () => {
            setLoading(true);

            if (!selectedDate) {
                setLoading(false);
                return;
            }

            // Format the selected date to "yyyy-MM"
            const formattedDate = format(selectedDate, 'yyyy-MM');
            if (activeView === 'noll') {
                    await fetchNullReporting(formattedDate);
                    setLoading(false);
                    return;
                }
            try {
                // Fetch supplier data
                const data = await API.Suppliers.GetSupplierDataByMonth(formattedDate);

                // Fetch all thresholds in one call
                const allThresholds = await API.Suppliers.GetAllThresholds();

                // Create a mapping of thresholds based on OrgNr
                const thresholdMap = new Map();
                allThresholds.forEach((threshold: any) => {
                    thresholdMap.set(threshold.orgNr, {
                        limitYellow: threshold.limitYellow,
                        limitRed: threshold.limitRed
                    });
                });

                // Enhance supplier data with thresholds by matching OrgNr
                const enhancedData = data.map((supplier: Supplier) => {
                    const thresholds = thresholdMap.get(supplier.orgNumber) || { limitYellow: 20, limitRed: 40 }; // Defaults if no match
                    return {
                        ...supplier,
                        thresholdYellow: thresholds.limitYellow,
                        thresholdRed: thresholds.limitRed
                    };
                });

                setSupplierAPIData(enhancedData);
                setLoading(false);
            } catch (err) {
                setError((err as any).message);
                setLoading(false);
            }
        };

        fetchSupplierData();
    }, [selectedDate,activeView]);
const handleRefresh = async () => {
  if (!selectedDate) return;
  const period = format(selectedDate, "yyyy-MM");
  setRefreshing(true);
  try {
    await API.NullReporting.RefreshSnapshots(period);
    await fetchNullReporting(period);
  } catch (e: any) {
    alert(e?.message ?? "Refresh misslyckades");
  } finally {
    setRefreshing(false);
  }
};


    const onSearchChange = (newValue: string) => {
        setSearchText(newValue);
    };

    const onDateChange = (date: Date | null) => {
        setSelectedDate(date);
    };

    const onFilterChange = (filter: string) => {
        setFilter(filter);
    };
const runStatus = getRunStatusDetails(nullLastRun?.status);
const lastUpdatedText = nullLastRun
  ? formatLastRunTime(nullLastRun.endedAt ?? null, nullLastRun.startedAt ?? null)
  : "—";

    return (
        <div className="review-suppliers-container">
          
            <h1 className="review-suppliers-header">Leverantörer</h1>

            {/* Search controls */}
            <div className="search-controls">
                <div className="search-bar-wrapper">
                    <SearchBar placeholder="Sök..." value={searchText} onChange={onSearchChange} />
                </div>
                <MonthPicker
                    selectedDate={selectedDate}
                    onDateChange={onDateChange}
                    loading={loading}  // Pass loading prop
                />
                <div className="filter-button-wrapper">
                    <FilterButton onFilterChange={onFilterChange} currentFilter={filter} />

                </div>
                <Button onClick={() => { setAValueInfoModal(true); }} style={{ marginLeft: 10 }} className='btn button-cancel'><FontAwesomeIcon icon={faInfoCircle} /></Button>
                <div style={{ display: 'flex',marginLeft:8 }}>
   <ButtonGroup className="followup-toggle-group">
    <ToggleButton 
            type="radio"
            variant="outline-primary"
            checked={activeView === 'avvikelse'}
            value="sending"
            onClick={() => setActiveView('avvikelse')}
            className={`followup-btn ${activeView === 'avvikelse' ? 'active-btn' : 'inactive-btn'}`}
            >
                Statistik
    </ToggleButton>
    <ToggleButton
            type="radio"
            variant="outline-primary"
            checked={activeView === 'noll'}
            value="sending"
            onClick={() => setActiveView('noll')}
            className={`followup-btn ${activeView === 'noll' ? 'active-btn' : 'inactive-btn'}`}
    >
        Nollrapporter
    </ToggleButton>
   </ButtonGroup>
</div>
            </div>
{activeView === "noll" && (
  <>
    <section className="nr-runmeta-card">
      <div className="nr-runmeta-row">
        <div className="nr-runmeta-col">
          <h1 className="nr-runmeta-label">Status</h1>
          <div className={`nr-runstatus ${runStatus.colorClass}`}>
            <span className="nr-runstatus-icon">{runStatus.icon}</span>
            <span className="nr-runstatus-text" style={{fontFamily:'lora'}}>{runStatus.text}</span>
          </div>
        </div>

        <div className="nr-runmeta-divider" />

        <div className="nr-runmeta-col">
          <h2 className="nr-runmeta-label">Senast körd</h2>
          <p className="nr-runmeta-value" style={{fontFamily:'lora'}}>{lastUpdatedText}</p>

          {/* valfritt: visa backend-message om fail */}
          {nullLastRun?.status === 3 && nullLastRun?.message && (
            <p className="nr-runmeta-hint">Fel: {nullLastRun.message}</p>
          )}
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className={`nr-runmeta-refresh ${refreshing ? "is-loading" : ""}`}
          title="Kör uppdatering"
        >
          <FontAwesomeIcon icon={faSyncAlt} className={refreshing ? "spin" : ""} />
        </button>
      </div>
    </section>
  </>
)}
            {/* Supplier Table */}
            <div className="supplier-table-wrapper">
                 {activeView === 'avvikelse' ? (
    <SupplierTable
      supplierData={supplierAPIData}
      isLoading={loading}
      selectedDate={selectedDate}
      filter={filter}
      searchText={searchText}
    />
  ) : (
    <NullReportingTable
      items={nullSnapshots}
      isLoading={nullLoading}
      searchText={searchText}
       selectedDate={selectedDate}
      filter={filter}
    />
  )}
            </div>
            {
                showAValueInfoModal &&
                <AValueInfoModal activeView={activeView} onDismiss={() => { setAValueInfoModal(false); }} />
            }
        </div>
    );
};

export default ReviewSuppliers;